import { createStyles } from 'antd-style';
import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface BreadCrumbItem {
  title: string;
  path?: string;
  order: number;
}

interface BreadCrumbContextProps {
  breadCrumbs: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] | undefined;
  addBreadCrumbs: (breadCrumbItems: BreadCrumbItem[]) => void;
  clearBreadCrumbs: () => void;
}

const BreadCrumbContext = createContext<BreadCrumbContextProps>({
  breadCrumbs: undefined,
  addBreadCrumbs: (breadCrumbItems: BreadCrumbItem[]) => breadCrumbItems,
  clearBreadCrumbs: () => null
});

interface BreadCrumbProviderProps {
  children: React.ReactNode;
}

const useStyles = createStyles((props: { isDarkMode: boolean }) => {
  return {
    pointer: {
      cursor: 'pointer'
    }
  };
});

function BreadCrumbContextProvider({ children }: BreadCrumbProviderProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { cx, styles } = useStyles();
  const { pathname } = location;

  const [breadCrumbs, setBreadCrumbs] =
    useState<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[]>();
  const [breadCrumbItems, setBreadCrumbItems] = useState<BreadCrumbItem[] | undefined>(undefined);

  useEffect(() => {
    setBreadCrumbs(getBreadCrumbs());
  }, [pathname, breadCrumbItems]);

  function getBreadCrumbs(): Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] | undefined {
    if (!breadCrumbItems) return undefined;

    if (breadCrumbItems.length == 1) return undefined;

    const breadCrumbs: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] = [];

    const orderBreadCrumbs = breadCrumbItems.sort(({ order: a }, { order: b }) => a - b);

    for (let i = 0; i < breadCrumbItems.length; i++) {
      if (i !== orderBreadCrumbs.length - 1 && orderBreadCrumbs[i].path) {
        breadCrumbs.push({
          title: orderBreadCrumbs[i].title,
          className: cx(styles.pointer),
          onClick: () => {
            navigate(orderBreadCrumbs[i].path!);
          }
        });
      } else {
        breadCrumbs.push({
          title: orderBreadCrumbs[i].title
        });
      }
    }

    return breadCrumbs;
  }

  function addBreadCrumbs(breadCrumbItems: BreadCrumbItem[]) {
    setBreadCrumbItems(breadCrumbItems);
    // this was an attempt to be able to 'cascade' breacdrumbs and set them in seperate files
    // unfortunately useeffects dont happen in order and it didnt work since sometimes the useeffect that used to be in his file would erase the others.
    // so for now each file has to set all the bread crumbs they want
    // setBreadCrumbItems(prevState => {
    //   if (prevState) {
    //     let newState = prevState;
    //     for (let i = 0; i < breadCrumbItems.length; i++) {
    //       if (newState.some(x => x.order === breadCrumbItems[i].order)) {
    //         newState[newState.findIndex(s => s.order === breadCrumbItems[i].order)] = {
    //           title: breadCrumbItems[i].title,
    //           path: breadCrumbItems[i].path,
    //           order: breadCrumbItems[i].order
    //         };
    //       } else {
    //         newState.push({
    //           title: breadCrumbItems[i].title,
    //           path: breadCrumbItems[i].path,
    //           order: breadCrumbItems[i].order
    //         });
    //       }
    //     }

    //     return [...newState];
    //   } else {
    //     return breadCrumbItems.map(x => {
    //       return {
    //         title: x.title,
    //         path: x.path,
    //         order: x.order
    //       };
    //     });
    //   }
    // });
  }

  function clearBreadCrumbs() {
    setBreadCrumbItems(undefined);
  }

  const value = {
    breadCrumbs,
    addBreadCrumbs,
    clearBreadCrumbs
  };

  return <BreadCrumbContext.Provider value={value}>{children}</BreadCrumbContext.Provider>;
}

const BreadCrumbContextConsumer = BreadCrumbContext.Consumer;

const useBreadCrumbContext = () => useContext(BreadCrumbContext);

export { BreadCrumbContextConsumer, BreadCrumbContextProvider, useBreadCrumbContext };
